import { queryOptions, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import axios from 'axios'
import { QueryError } from 'errors'
import { PlaceConfig } from 'types/places'
import { Place } from 'types/products'

export const placesQueryOptions = queryOptions<Array<Place & { id: string }>>({
  queryKey: ['places'],
  queryFn: async () => {
    return (await axios.get('/places')).data
  },
})

export function useGetPlaces() {
  return useSuspenseQuery<Array<Place & { id: string }>>(placesQueryOptions)
}

export function usePlacesConfigs() {
  return useQuery<Array<Place & { id: string }>, QueryError, Array<PlaceConfig>>({
    ...placesQueryOptions,
    select: (places: Array<Place>) => {
      // for each possible rack number, get the highest number of columns and shelves
      const racks = Array.from(new Set(places.map((place) => place.rack))).sort((a, b) => a - b)
      return racks.map((rack) => {
        return {
          rack: rack,
          numberOfColumns: Math.max(...places.filter((p) => p.rack === rack).map((p) => p.column)),
          numberOfShelfes: Math.max(...places.filter((p) => p.rack === rack).map((p) => p.shelf)),
        }
      })
    },
  })
}

export function useDeleteRack() {
  const queryClient = useQueryClient()

  return useMutation<void, QueryError, number>({
    mutationFn: async (rack: number) => {
      await axios.delete(`/places/rack/${rack}`)
    },
    onSuccess: (_, rack) => {
      queryClient.setQueryData(['places'], (oldData: Array<Place & { id: string }> | undefined) => {
        if (!oldData) return oldData
        return oldData.filter((place) => place.rack !== rack)
      })
    },
    meta: { successMessage: 'Regał został usunięty' },
  })
}

export function useAddRack() {
  const queryClient = useQueryClient()

  return useMutation<void, QueryError, PlaceConfig>({
    mutationFn: async (placeConfig: PlaceConfig) => {
      await axios.post('/places/rack', placeConfig)
    },
    meta: { successMessage: 'Regał został dodany', closeModal: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['places'] })
    },
  })
}

export function useUpdateRack() {
  const queryClient = useQueryClient()

  return useMutation<PlaceConfig, QueryError, PlaceConfig>({
    mutationFn: async (placeConfig: PlaceConfig) => {
      return (await axios.put('/places/rack', placeConfig)).data
    },
    meta: { successMessage: 'Regał został zaktualizowany', closeModal: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['places'] })
    },
  })
}
